import React, { useContext, useEffect, useState } from "react";
import { useSelector } from "react-redux";

import { useDispatch } from "react-redux";
import moment from "moment";
import { Col, Row } from "react-bootstrap";
import Thumbnail from "src/components/thumbnail";

import {
  fetchLearningsInsightsReport,
  fetchTeamsLearningsReport,
} from "src/redux/actions/coaching/reports";
import InfiniteScroll from "src/components/infiniteScroll";
import Loading from "src/components/loading";

import cx from "classnames";
import _ from "lodash";
import styles from "./insights.module.scss";
import { Link } from "react-router-dom";
import Actions from "src/components/actionbar";
import DownloadExcel from "src/components/downloadExcel";
import { CourseReportContext } from "../../../context";
import Text from "src/components/text";

const defaultAssetImage = "/images/defaultIcons/default_asset.jpg";

const LEARNINGS_REPORT_SCHEMA = [
  { column: "Name", type: String, value: (row) => row.name },
  { column: "Assigned By", type: String, value: (row) => row.assignedBy },
  { column: "Enrolled Date", type: String, value: (row) => row.enrolledDate },
  { column: "Completed Date", type: String, value: (row) => row.completedDate },
  { column: "Status", type: String, value: (row) => row.status },
];

const InsightsReport: React.FC = () => {
  const dispatch = useDispatch();

  const [query, setQuery] = useState<string | null>("");
  const [sortPreference, setSortPreference] = useState<{
    sortBy: string;
    orderBy: string;
  }>({ sortBy: "enrolledDate", orderBy: "ASC" });

  const reportContext = useContext(CourseReportContext);

  const {
    loading,
    loaded,
    learnings,
    currentPage,
    nextPage,
    hasMoreItems,
    pagination,
  } = useSelector((state: any) => {
    const learningsState = _.get(state.coaching, "reports.learnings.insights");
    const loading = _.get(learningsState, "loading");
    const loaded = _.get(learningsState, "loaded");
    const pagination = _.get(learningsState, "pagination");
    const currentPage = _.get(pagination, "currentPage", 0);
    const total = _.get(pagination, "totalPages", 0);
    const hasMoreItems = _.get(pagination, "hasMoreItems");

    const learnings = _.get(learningsState, "items") || [];

    let nextPage: number | null = _.get(pagination, "currentPage", 0) + 1;
    if (nextPage && nextPage >= total) {
      nextPage = null;
    }

    return {
      loading: loading,
      loaded: loaded,
      learnings: learnings,
      currentPage: currentPage,
      nextPage: nextPage,
      hasMoreItems: hasMoreItems,
      pagination: pagination,
    };
  }, _.isEqual);

  const fetchState = {
    loading: loading,
    succeeded: loaded,
    error: false,
  };

  const fetchLearningsForPage = (query, page) => {
    const request = {
      query: query,
      page: page,
      limit: 10,
      sort: sortPreference,
      startDate: reportContext.startDate, 
      endDate: reportContext.endDate
    };

    dispatch(fetchLearningsInsightsReport(request));
  };

  const handleLoadMore = () => {
    if (nextPage) {
      fetchLearningsForPage(query, nextPage);
    }
  };

  const getCourseStatus = (item) => {
    if (item.completed > 0) {
      return `completed with ${((item?.averageScore || 0.0) * 100).toFixed(0)}%`;
    }

    if (item.inProgress > 0) {
      return `in progress`;
    }

    if (item.notStarted > 0) {
      return `not started`;
    }
  };

  const handleSort = (sortBy: string, orderBy: string) => {
    setSortPreference({ sortBy, orderBy });
  };

  const renderSort = (orderA: string, orderB: string, sort: string) => {
    return (
      <span
        className={cx(styles.sort, {
          [styles.hideSort]: sortPreference.sortBy !== sort,
        })}
      >
        <i
          className={cx("fa-solid fa-angle-up", {
            [styles.active]:
              sortPreference.orderBy === orderA &&
              sortPreference.sortBy === sort,
          })}
          onClick={() => handleSort(sort, orderA)}
        ></i>
        <i
          className={cx("fa-solid fa-angle-down", {
            [styles.active]:
              sortPreference.orderBy === orderB &&
              sortPreference.sortBy === sort,
          })}
          onClick={() => handleSort(sort, orderB)}
        ></i>
      </span>
    );
  };

  const getDateFormat = (date) => {
    if (date) {
      return moment(date).format("DD MMM YYYY");
    }
    return "N/A";
  };

  const formatDataForDownload = (data) => {
    if (_.isEmpty(data)) {
      return [];
    }

    return data.map((learning) => ({
      name: learning?.course.name ?? "N/A",
      assignedBy: learning.assignedBy ? learning.assignedBy.firstName : "N/A",
      enrolledDate: learning.enrolledDate ? moment(learning.enrolledDate).format("YYYY-MMM-DD") : "N/A",
      completedDate: learning.completedDate ? moment(learning.completedDate).format("YYYY-MMM-DD") : "N/A",
      status: getCourseStatus(learning),
    }));
  };

  const handleQueryChange = (query: any) => {
    setQuery(query || "");
  };

  useEffect(() => {
    fetchLearningsForPage(query, 0);
  }, [query]);

  useEffect(() => {
    fetchLearningsForPage(query, 0);
  }, [sortPreference]);

  return (
    <section className={styles.container}>
      <div className={styles.header}>
        <div className={styles.heading}>
          <h3>course insights: participation, progress & results</h3>
          <span className={styles.description}>
            measure learning impact with every course
          </span>
        </div>
        <div className={styles.actions}>
          <Actions.Search query={query} onSearch={handleQueryChange} />
          {_.size(learnings) > 0 && (
            <div className={styles.downloadButton}>
              <DownloadExcel
                title="Course Insights Report"
                fileName="CourseInsightsReport"
                schema={LEARNINGS_REPORT_SCHEMA}
                data={formatDataForDownload(learnings)}
                fetchState={fetchState}
                pagination={pagination}
                fetchDataForPage={(page) => fetchLearningsForPage("", page + 1)}
                invalidateData={() => {}}
              />
            </div>
          )}
        </div>
      </div>

      <div className={cx(styles.learningTable)}>
        {!loading && _.size(learnings) > 0 && (
          <Row className={styles.tableHeader}>
            <Col lg={5} className={styles.heading}>
              courses
            </Col>
            <Col lg={1} className={styles.heading}>
              assigned by
            </Col>
            <Col lg={2} className={styles.heading}>
              enrolled date
              {renderSort("ASC", "DESC", "enrolledDate")}
            </Col>
            <Col lg={2} className={styles.heading}>
              completed date
              {renderSort("ASC", "DESC", "completedDate")}
            </Col>
            <Col lg={2} className={styles.heading}>
              status
            </Col>
          </Row>
        )}

        <InfiniteScroll
          className={styles.learnersList}
          loading={loading}
          hasMore={hasMoreItems}
          loadMore={handleLoadMore}
          loader={() => {
            return _.times(
              _.size(learnings) === 0 || currentPage == 0 ? 4 : 1,
              (index) => <PlaceholderLearning key={index} />,
            );
          }}
        >
          {_.size(learnings) === 0 && !loading && (
            <span className={styles.noRecordsFound}>No results found</span>
          )}

          {_.size(learnings) > 0 &&
            learnings.map((item, index) => {
              const completedDate = getDateFormat(item.completedDate);
              const enrolledDate = getDateFormat(item.enrolledDate);

              return (
                <Row key={index} className={styles.tableRow}>
                  <Col lg={5} className={styles.info}>
                    <Thumbnail
                      media={item.course.banner?.background}
                      defaultImage={defaultAssetImage}
                      className={styles.thumbnail}
                    />
                    <div className={styles.details}>
                      <Link
                        to={`/coaching/manage/courses/draft/${item.course.id}`}
                        className={styles.title}
                      >
                      <Text className={styles.name} text={item?.course?.name || ""} lines={1} />
                        
                      </Link>
                      <span className={styles.type}>
                        {_.capitalize(item.type)}
                      </span>
                    </div>
                  </Col>
                  <Col lg={1} className={styles.assignedUsers}>
                    {item.assignedBy?.firstName || "N/A"}
                  </Col>
                  <Col lg={2} className={styles.progress}>
                    <div className={styles.progressInfo}>
                      <span className={styles.enrolled}>{enrolledDate}</span>
                    </div>
                  </Col>
                  <Col lg={2} className={styles.createdDate}>
                    {completedDate}
                  </Col>
                  <Col lg={2} className={styles.avgScore}>
                    {getCourseStatus(item)}
                  </Col>
                </Row>
              );
            })}
        </InfiniteScroll>
      </div>
    </section>
  );
};

export default InsightsReport;

const PlaceholderLearning = () => {
  return (
    <>
      <Row className={cx(styles.tableRow, styles.placeholder)}>
        <Col lg={5} className={styles.info}>
          <div className={cx(styles.thumbnail, styles.block)}></div>
          <div className={styles.details}>
            <h4 className={cx(styles.title, styles.block)}></h4>
            <span className={cx(styles.type, styles.block)}></span>
          </div>
        </Col>
        <Col lg={2} className={styles.assignedUsers}>
          <div className={styles.block}></div>
        </Col>
        <Col lg={2} className={styles.progress}>
          <div className={styles.block}></div>
        </Col>
        <Col lg={2} className={styles.createdDate}>
          <div className={styles.block}></div>
        </Col>
        <Col lg={1} className={styles.avgScore}>
          <div className={styles.block}></div>
        </Col>
      </Row>
    </>
  );
};
